import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/kfmApi";
import { X, Send, Loader2, Sparkles, BookOpen } from "lucide-react";

const OPENER = "What can I help you with today? Or feel free to browse our extensive help section via the tab below.";

// Per-screen quick questions (the "smart per-screen tips").
function getPageTips(pathname) {
  const p = pathname || "";
  if (/^\/study\/[^/]+/.test(p))
    return { label: "a Study session", tips: ["How do I listen to this study?", "How do I compare translations?", "How do I look up a Hebrew word?", "How do I save a verse?"] };
  if (p === "/study")
    return { label: "the Start a Study screen", tips: ["How do I start a study?", "Can I study a topic instead of a passage?", "What is “Go Deeper”?"] };
  if (p.startsWith("/voice-notes"))
    return { label: "the Voice Notes screen", tips: ["How do I record a voice note?", "How do I turn a note into a study?", "What does the “Explored” badge mean?"] };
  if (p.startsWith("/history"))
    return { label: "the History screen", tips: ["Where are my saved verses?", "How do I reopen a past study?", "How do I remove a saved item?"] };
  if (p.startsWith("/journal"))
    return { label: "the Prayer Journal", tips: ["How do I save a prayer?", "How do I turn a prayer into a study?"] };
  if (p.startsWith("/search"))
    return { label: "Bible Search", tips: ["How does search work?", "How do I open a result as a study?"] };
  if (p.startsWith("/settings"))
    return { label: "Settings", tips: ["How do I set my default translation?", "How do I change the reading voice?", "How do I manage my plan?"] };
  if (p.startsWith("/billing") || p.startsWith("/pricing"))
    return { label: "the Plans & Billing screen", tips: ["What are credits?", "What are top-ups?", "How do I cancel or change my plan?"] };
  if (p.startsWith("/rooms"))
    return { label: "Study Together", tips: ["How do I start a shared study?", "How do others join my room?"] };
  if (p.startsWith("/resources"))
    return { label: "the Resources screen", tips: ["What can this app do?", "How do I look up original-language words?"] };
  return { label: "the app", tips: ["How do I get around on my phone?", "How do I start a study?", "What are credits?", "How do I change my Bible translation?"] };
}

// A free, controlled help assistant panel (opened from the Help tab / sidebar link — no floating
// launcher). Answers how-to questions grounded in the app's Help knowledge, opens with a fixed
// greeting + per-screen quick questions, and links out to the full written help section.
// Not a theology bot — it redirects Bible questions to the Study tab.
export default function HelpAssistant({ open, onClose }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const page = getPageTips(pathname);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/help/chat", { message: msg, history, page: page.label });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — I couldn't answer just now. Please try again, or use the contact form on the Help page." },
      ]);
    }
    setSending(false);
  };

  const openDocs = () => {
    onClose?.();
    navigate("/help");
  };

  if (!open) return null;

  return (
    <div
      data-testid="help-assistant-panel"
      className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-[60] w-[calc(100vw-2rem)] max-w-sm h-[30rem] max-h-[calc(100vh-8rem)] md:max-h-[calc(100vh-3rem)] flex flex-col rounded-3xl bg-[#0E131F] border border-[#1E273D] shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-[#141B29] text-white shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#0E131F]/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.75} />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-medium">Help Assistant</div>
            <div className="text-[11px] text-white/50">Here to help you use the app</div>
          </div>
        </div>
        <button data-testid="help-assistant-close" onClick={onClose} className="text-white/60 hover:text-white transition-colors">
          <X className="w-5 h-5" strokeWidth={1.75} />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#07090E]">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p data-testid="help-greeting" className="text-sm text-slate-400 leading-relaxed px-1">
              {OPENER}
            </p>
            <div className="flex flex-wrap gap-2">
              {page.tips.map((s) => (
                <button
                  key={s}
                  data-testid="help-suggestion"
                  onClick={() => send(s)}
                  className="text-xs text-[#00E5FF] border border-[#00E5FF]/25 hover:bg-[#10222A] rounded-full px-3 py-1.5 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              data-testid={`help-msg-${m.role}`}
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-[#00E5FF] text-[#07090E]" : "bg-[#0E131F] border border-[#1E273D] text-slate-300"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-[#0E131F] border border-[#1E273D] rounded-2xl px-4 py-3">
              <Loader2 className="w-4 h-4 text-[#00E5FF] animate-spin" />
            </div>
          </div>
        )}
      </div>

      {/* Browse full help section (the "tab below") */}
      <button
        data-testid="help-browse-docs"
        onClick={openDocs}
        className="flex items-center justify-center gap-2 py-2.5 border-t border-[#1E273D] bg-[#0E131F] text-sm font-medium text-[#00E5FF] hover:bg-[#10222A]/60 transition-colors shrink-0"
      >
        <BookOpen className="w-4 h-4" strokeWidth={1.75} /> Browse the full help section
      </button>

      {/* Input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="flex items-center gap-2 p-3 border-t border-[#1E273D] bg-[#0E131F] shrink-0"
      >
        <input
          data-testid="help-assistant-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your question…"
          className="flex-1 h-11 rounded-full border border-[#29344D] px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/25"
        />
        <button
          data-testid="help-assistant-send"
          type="submit"
          disabled={sending || !input.trim()}
          className="w-11 h-11 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] flex items-center justify-center disabled:opacity-40 transition-colors shrink-0"
        >
          <Send className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </form>
    </div>
  );
}
